import { useTranslation } from 'react-i18next';

import { CatalogView } from '@lemoncloud/codes-goods-api';
import { CatalogImageSlider } from '@web/app/shared';
import { X } from 'lucide-react';

import { getCurrencySymbol } from '@codes/react-utils';
import {
    Avatar,
    AvatarImage,
    Badge,
    Label,
    Sheet,
    SheetClose,
    SheetContent,
    SheetDescription,
    SheetHeader,
    SheetTitle,
    SheetTrigger,
} from '@codes/ui-kit';

interface DependencyListProps {
    dependencies: CatalogView['dependency$$'];
    sliceIdx?: number;
    getIconImage: (catalog: CatalogView | undefined) => string;
}

export const DependencyList = ({ dependencies, sliceIdx = 0, getIconImage }: DependencyListProps) => {
    const { t, i18n } = useTranslation();

    if (!dependencies || dependencies.length === 0) {
        return null;
    }

    const slicedDependencies = sliceIdx > 0 ? dependencies.slice(0, sliceIdx) : dependencies;

    return (
        <ul className="grid max-xs:grid-cols-1 grid-cols-2 lg:grid-cols-3 gap-3">
            {slicedDependencies.map(dependency => (
                <li key={dependency.id}>
                    <Sheet>
                        <SheetTrigger asChild>
                            <button className="rounded-xl border border-detail-line bg-card w-full flex flex-col px-4 pt-4 pb-4 transition-all duration-200 hover:border-point/30 hover:shadow-sm">
                                <Avatar>
                                    <AvatarImage src={getIconImage(dependency as CatalogView)} />
                                </Avatar>
                                <div className="font-medium text-title text-start truncate min-h-[25px] mt-2">
                                    {dependency.name}
                                </div>
                                <div className="text-sm text-sub leading-relaxed max-h-[76px] overflow-auto text-start mt-1">
                                    {(i18n.language === 'en' && dependency?.summaryEn) || dependency?.summary}
                                </div>
                                <div className="flex pt-2 flex-1 items-end">
                                    <Badge variant="B">{dependency?.stereo}</Badge>
                                </div>
                            </button>
                        </SheetTrigger>
                        <SheetContent className="overflow-auto">
                            <SheetHeader>
                                <SheetTitle>{dependency.name}</SheetTitle>
                                <SheetClose>
                                    <X className="w-5 h-5" />
                                </SheetClose>
                            </SheetHeader>
                            <div className="mt-4">
                                <CatalogImageSlider
                                    images={dependency?.images}
                                    name={dependency?.name}
                                    stereo={dependency?.stereo}
                                    iconImage={getIconImage(dependency as CatalogView)}
                                />
                            </div>

                            <div className="flex flex-col gap-y-2 mt-[18px]">
                                <div className="flex items-center gap-x-2">
                                    <Avatar>
                                        <AvatarImage src={getIconImage(dependency as CatalogView)} />
                                    </Avatar>
                                    <div className="font-medium text-title text-start">{dependency.name}</div>
                                </div>
                                <SheetDescription className="text-start text-sub text-sm">
                                    {(i18n.language === 'en' && dependency?.summaryEn) || dependency?.summary}
                                </SheetDescription>
                            </div>

                            <div className="h-px bg-divider my-7" />

                            {/* Configuration Information */}
                            {dependency.config$ && Object.keys(dependency.config$).length > 0 && (
                                <>
                                    <Label className="text-[16px] mb-2">{t('serviceDetail.configuration')}</Label>
                                    <div className="rounded-xl bg-box border border-detail-line p-3 mb-4">
                                        {dependency.config$.runtime && (
                                            <div className="flex justify-between items-center text-sm mb-2">
                                                <span className="text-sub">{t('serviceDetail.runtime')}:</span>
                                                <span className="text-title">{dependency.config$.runtime}</span>
                                            </div>
                                        )}
                                        {dependency.config$.region && (
                                            <div className="flex justify-between items-center text-sm">
                                                <span className="text-sub">{t('serviceDetail.region')}:</span>
                                                <span className="text-title">{dependency.config$.region}</span>
                                            </div>
                                        )}
                                    </div>
                                </>
                            )}

                            {/* Price Information */}
                            {dependency.price$ && (
                                <>
                                    <Label className="text-[16px] mb-2">{t('serviceDetail.serviceFee')}</Label>
                                    <div className="rounded-xl bg-box border border-detail-line p-3 mb-4">
                                        {dependency.price$.initPrice !== undefined && (
                                            <div className="flex justify-between items-center text-sm mb-2">
                                                <span className="text-sub">{t('price.service.initial_setup_fee')}</span>
                                                <span className="text-title">
                                                    {getCurrencySymbol(dependency.price$.currency)}
                                                    {dependency.price$.initPrice}
                                                </span>
                                            </div>
                                        )}
                                        {dependency.price$.trafficPrice !== undefined && (
                                            <div className="flex justify-between items-center text-sm mb-2">
                                                <span className="text-sub">{t('price.service.traffic_cost')}</span>
                                                <span className="text-title">
                                                    {getCurrencySymbol(dependency.price$.currency)}
                                                    {dependency.price$.trafficPrice}
                                                </span>
                                            </div>
                                        )}
                                        {dependency.price$.hourPerPrice !== undefined && (
                                            <div className="flex justify-between items-center text-sm mb-2">
                                                <span className="text-sub">{t('price.service.cost_per_hour')}</span>
                                                <span className="text-title">
                                                    {getCurrencySymbol(dependency.price$.currency)}
                                                    {dependency.price$.hourPerPrice}
                                                </span>
                                            </div>
                                        )}
                                        {dependency.price$.callPerPrice !== undefined && (
                                            <div className="flex justify-between items-center text-sm">
                                                <span className="text-sub">{t('price.service.per_call')}</span>
                                                <span className="text-title">
                                                    {getCurrencySymbol(dependency.price$.currency)}
                                                    {dependency.price$.callPerPrice}
                                                </span>
                                            </div>
                                        )}
                                    </div>
                                </>
                            )}

                            {/* Settings/Environment Variables */}
                            {dependency.setting$ && dependency.setting$.length > 0 && (
                                <>
                                    <Label className="text-[16px] mb-2">{t('serviceDetail.settings')}</Label>
                                    <div className="rounded-xl bg-box border border-detail-line p-3 mb-4 max-h-40 overflow-y-auto">
                                        {dependency.setting$
                                            .filter(setting => !setting.isHidden)
                                            .slice(0, 5)
                                            .map((setting, index) => (
                                                <div key={index} className="text-sm mb-2 last:mb-0">
                                                    <div className="font-medium text-sub">{setting.name}</div>
                                                    <div className="text-xs text-placeholder">
                                                        {setting.description}
                                                    </div>
                                                </div>
                                            ))}
                                        {dependency.setting$.filter(setting => !setting.isHidden).length > 5 && (
                                            <div className="text-xs text-placeholder mt-2">
                                                {t('serviceDetail.moreSettings', {
                                                    count:
                                                        dependency.setting$.filter(setting => !setting.isHidden)
                                                            .length - 5,
                                                })}
                                            </div>
                                        )}
                                    </div>
                                </>
                            )}
                        </SheetContent>
                    </Sheet>
                </li>
            ))}
        </ul>
    );
};
